import { useAuth } from "@dl/web-core-js/auth";
import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import {
  Card,
  Field,
  Input,
  MultiSelect,
  Select,
  entityOptions,
  enumOptions,
} from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  PRODUCT_STATUSES,
  type Country,
  type NamedDTO,
  type ProductDTO,
} from "../../api/product";

/**
 * Identity, classification and headline pricing.
 *
 * The split between editable and read-only here is not cosmetic — it mirrors
 * where each value actually comes from:
 *
 *   - SKU, UPC, harmonized code, origin: entered by a human, editable.
 *   - Category, designer, tags, collections: references to other tables, so
 *     they are pickers rather than free text.
 *   - Type: fixed at creation. Changing it would change which tabs exist and
 *     what the product means to the bundle logic, so it is not an inline edit.
 *   - COGS: produced by the costing run. An edit here would be silently
 *     overwritten, so it is shown disabled with its source named.
 */
export function GeneralTab({
  draft,
  onChange,
  readOnly,
}: {
  draft: ProductDTO;
  onChange: (next: ProductDTO) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();

  const upc = draft.metadata?.upc ?? "";
  const upcInvalid = upc !== "" && !/^\d{12}$/.test(upc);
  // Bundle pricing rolls up from the component items, so editing it on the
  // bundle would be overwritten — same rule the Vaadin form enforced.
  const priceLocked = readOnly || draft.type === "BUNDLE";

  // Reference lists change rarely and are shared by every product page.
  const categories = useReference("category", http, "/product/category");
  const designers = useReference("designer", http, "/product/designer");
  const collections = useReference("collection", http, "/product/collection");
  const tags = useReference("tag", http, "/product/tag");

  const setMetadata = (key: string, value: string) =>
    onChange({ ...draft, metadata: { ...(draft.metadata ?? { sku: "" }), [key]: value } });

  const setReference = (key: "category" | "designer", id: string, list: NamedDTO[]) =>
    onChange({ ...draft, [key]: id === "" ? null : (list.find((e) => String(e.id) === id) ?? null) });

  const setMany = (key: "tags" | "collections", ids: string[], list: NamedDTO[]) =>
    onChange({ ...draft, [key]: list.filter((entry) => ids.includes(String(entry.id))) });

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <h3 className="mb-4 text-[13px] font-semibold text-ink">
          {t("product.section.identity", "Identity")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          <Field label={t("column.sku", "SKU")} htmlFor="sku" required>
            <Input
              id="sku"
              value={draft.metadata?.sku ?? ""}
              readOnly={readOnly}
              onChange={(event) => setMetadata("sku", event.target.value)}
            />
          </Field>
          <Field
            label={t("column.upc", "UPC")}
            htmlFor="upc"
            error={
              upcInvalid ? t("product.upc.invalid", "Must be exactly 12 digits, or empty") : undefined
            }
          >
            <Input
              id="upc"
              value={draft.metadata?.upc ?? ""}
              readOnly={readOnly}
              invalid={upcInvalid}
              onChange={(event) => setMetadata("upc", event.target.value)}
            />
          </Field>
          <Field
            label={t("column.type", "Type")}
            htmlFor="type"
            hint={t("product.hint.type", "Set when the product is created")}
          >
            <Select id="type" value={draft.type} options={enumOptions([draft.type])} disabled />
          </Field>
          <Field label={t("column.status", "Status")} htmlFor="status">
            <Select
              id="status"
              value={draft.status}
              disabled={readOnly}
              options={enumOptions(PRODUCT_STATUSES)}
              onChange={(event) =>
                onChange({ ...draft, status: event.target.value as ProductDTO["status"] })
              }
            />
          </Field>
          <Field label={t("column.harmonized-code", "Harmonized code")} htmlFor="hs">
            <Input
              id="hs"
              value={draft.metadata?.harmonized_code ?? ""}
              readOnly={readOnly}
              onChange={(event) => setMetadata("harmonized_code", event.target.value)}
            />
          </Field>
          <Field label={t("column.country-of-origin", "Country of origin")} htmlFor="origin">
            <Select
              id="origin"
              value={draft.metadata?.country_of_origin ?? ""}
              disabled={readOnly}
              placeholder=""
              options={enumOptions(["CANADA", "USA"] as Country[])}
              onChange={(event) =>
                onChange({
                  ...draft,
                  metadata: {
                    ...(draft.metadata ?? { sku: "" }),
                    country_of_origin: (event.target.value || null) as Country | null,
                  },
                })
              }
            />
          </Field>
          <Field label={t("product.brand", "Brand")} htmlFor="brand">
            <Input
              id="brand"
              value={draft.metadata?.brand ?? ""}
              readOnly={readOnly}
              onChange={(event) => setMetadata("brand", event.target.value)}
            />
          </Field>
          <Field label={t("product.manufacturer", "Manufacturer")} htmlFor="manufacturer">
            <Input
              id="manufacturer"
              value={draft.metadata?.manufacturer ?? ""}
              readOnly={readOnly}
              onChange={(event) => setMetadata("manufacturer", event.target.value)}
            />
          </Field>
          <Field
            label={t("product.keywords", "Keywords")}
            htmlFor="keywords"
            className="sm:col-span-2"
          >
            <Input
              id="keywords"
              value={draft.metadata?.keywords ?? ""}
              readOnly={readOnly}
              onChange={(event) => setMetadata("keywords", event.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="mb-4 text-[13px] font-semibold text-ink">
          {t("product.section.classification", "Classification")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
          <Field label={t("column.category", "Category")} htmlFor="category">
            <Select
              id="category"
              value={draft.category?.id ?? ""}
              disabled={readOnly}
              placeholder=""
              options={entityOptions(categories)}
              onChange={(event) => setReference("category", event.target.value, categories)}
            />
          </Field>
          <Field label={t("column.designer", "Designer")} htmlFor="designer">
            <Select
              id="designer"
              value={draft.designer?.id ?? ""}
              disabled={readOnly}
              placeholder=""
              options={entityOptions(designers)}
              onChange={(event) => setReference("designer", event.target.value, designers)}
            />
          </Field>
          <Field label={t("product.collections", "Collections")} htmlFor="collections">
            <MultiSelect
              id="collections"
              disabled={readOnly}
              options={entityOptions(collections)}
              value={(draft.collections ?? []).map((entry) => String(entry.id))}
              onChange={(ids: string[]) => setMany("collections", ids, collections)}
            />
          </Field>
          <Field label={t("product.tags", "Tags")} htmlFor="tags">
            <MultiSelect
              id="tags"
              disabled={readOnly}
              options={entityOptions(tags)}
              value={(draft.tags ?? []).map((entry) => String(entry.id))}
              onChange={(ids: string[]) => setMany("tags", ids, tags)}
            />
          </Field>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="mb-4 text-[13px] font-semibold text-ink">
          {t("product.section.pricing", "Pricing")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-4">
          <Field
            label={t("column.cogs", "COGS")}
            htmlFor="cogs"
            hint={t("product.hint.cogs", "Computed by the costing run")}
          >
            <Input id="cogs" value={draft.pricing?.cogs ?? ""} disabled />
          </Field>
          <Field label={t("column.msrp", "MSRP")} htmlFor="msrp">
            <Input
              id="msrp"
              type="number"
              step="0.01"
              value={draft.pricing?.msrp ?? ""}
              readOnly={priceLocked}
              onChange={(event) =>
                onChange({
                  ...draft,
                  pricing: { ...(draft.pricing ?? {}), msrp: numberOrNull(event.target.value) },
                })
              }
            />
          </Field>
          <Field label={t("column.map", "MAP")} htmlFor="map">
            <Input
              id="map"
              type="number"
              step="0.01"
              value={draft.pricing?.map ?? ""}
              readOnly={priceLocked}
              onChange={(event) =>
                onChange({
                  ...draft,
                  pricing: { ...(draft.pricing ?? {}), map: numberOrNull(event.target.value) },
                })
              }
            />
          </Field>
          <Field
            label={t("product.created", "Created")}
            htmlFor="created"
            hint={t("product.hint.created", "Set by the system")}
          >
            <Input
              id="created"
              value={draft.created_at ? new Date(draft.created_at).toLocaleDateString() : ""}
              disabled
            />
          </Field>
        </div>
      </Card>
    </div>
  );
}

/** Shared loader for the small reference tables the pickers read from. */
function useReference(name: string, http: HttpClient, path: string): NamedDTO[] {
  const client = useMemo(() => new ResourceClient<NamedDTO>(http, path), [http, path]);
  const { data } = useQuery({
    queryKey: ["reference", name],
    queryFn: () => client.findAll(),
    staleTime: 30 * 60_000,
  });
  return data ?? [];
}

function numberOrNull(value: string): number | null {
  const parsed = Number(value);
  return value === "" || Number.isNaN(parsed) ? null : parsed;
}
